<!-- GroupedMultiselect.vue -->
<template>
  <div :id="id" class="grouped-multiselect">
    <AutoComplete
      ref="autocomplete"
      v-model="selectedValues"
      :suggestions="filteredOptions"
      @complete="search"
      @change="onSelectionChange"
      :completeOnFocus="true"
      multiple
      optionGroupLabel="heading"
      optionGroupChildren="items"
      optionLabel="text"
      @keydown.esc.stop
      @keydown="handleKeyDown"
      v-bind="$attrs">
    </AutoComplete>

    <!-- The way the data is encoded into the form is via a hidden input instead of a multiple select, so there's no need to append [] to the name -->
    <input type="hidden" :name="name" value="">
    <select :name="name" multiple class="hidden" ref="hiddenSelect">
      <option v-for="item in selectedValuesArray" :key="item.value" :value="item.value" selected></option>
    </select>
  </div>
</template>

<!-- options should be an array of hashes: [ { text: ..., value: ... }, ...] -->

<script setup>
  import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
  import AutoComplete from 'primevue/autocomplete';

  const props = defineProps({
    id:                     { type: String, required: true },
    name:                   { type: String, required: true },
    options:                { type: Array, required: true },
    initialSelectedValues:  { type: Array, default: () => [] }, // should be an array of strings
    maxItems:               { type: Number, default: 1000 },
  })

  // When props.options.length > 0, that means we're prepopulating the options,
  // which means initial values will be an array of integers.
  const initialValues = (props.options.length > 0)
    ? props.initialSelectedValues.map(value => {
       // Search through all groups to find the matching item
       for (const group of props.options) {
         const found = group.items.find(opt => opt.value?.toString() === value?.toString())
         if (found) return found
       }
       return undefined
      }).filter(item => item !== undefined)
    : props.initialSelectedValues

  const selectedValues = ref(initialValues)
  const options = ref(props.options)
  const filteredOptions = ref([])
  const autocomplete = ref(null)

  const selectedValuesArray = computed(() => {
    if (!selectedValues.value) return [];

    let selectedValuesArray = Array.isArray(selectedValues.value) ? selectedValues.value : [selectedValues.value]

    selectedValuesArray = selectedValuesArray.map(value => {
      // Search through all groups to find the matching item
      for (const group of options.value) {
        const found = group.items.find(opt => opt.value?.toString() === value?.value?.toString())
        if (found) return found
      }
      return undefined
    }).filter(item => item !== undefined && item.value !== '' && item.value !== null)
    return selectedValuesArray
  })

  // const selectedValuesArray = computed(() => {
  //   if (!selectedValues.value) return [];
  //   return Array.isArray(selectedValues.value) ? selectedValues.value : [selectedValues.value]
  // })

  const search = (event) => {
    const query = event.query.toLowerCase()
    filteredOptions.value = props.options
      .map(group => ({
        heading: group.heading,
        items: group.items.filter(item => {
          const notSelected = !selectedValuesArray.value.some(selected => selected.value?.toString() === item.value?.toString())

          const matchesQuery = item.text.toLowerCase().includes(query)

          return notSelected && matchesQuery
        })
      }))
      .filter(group => group.items.length > 0)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      // Don't show dropdown if we've reached maxItems
      if (selectedValuesArray.value.length >= props.maxItems) {
        return
      }

      // Trigger the search to show all items
      if (filteredOptions.value.length === 0) {
        search({ query: '' });
      }

      nextTick(() => {
        if (autocomplete.value && autocomplete.value.show) {
          autocomplete.value.show();
        }
      });
    }
  }

  const onSelectionChange = () => {
    search({ query:  '' })
  }

  const updateOptions = (event) => {
    // Handle custom setOptions event if needed
    search({ query: '' })
  }

  const updateSelectedValues = (event) => {
    // assuming an array
    selectedValues.value = event.detail.map(value => {
      for (const group of props.options) {
        const item = group.items.find(opt => opt.value === value)
        if (item) return item
      }
    })
    .filter(item => item !== undefined)

    search({ query: '' })
  }

  onMounted(() => {
    const el = document.getElementById(props.id)
    if (el) {
      el.addEventListener('setOptions', updateOptions)
      el.addEventListener('update:modelValue', updateSelectedValues)
    }
  })

  onUnmounted(() => {
    const el = document.getElementById(props.id)
    if (el) {
      el.removeEventListener('setOptions', updateOptions)
      el.removeEventListener('update:modelValue', updateSelectedValues)
    }
  })
</script>
